import { SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ECourseModality } from '@/shared/api/generated';
import { DatePicker } from '@/shared/ui/atoms';

import { useClassModuleForm } from '../../../context/ClassModuleFormContext';
import { CompanyAutocomplete } from '../../molecules/LookupAutocomplete';

const MODALITY_OPTIONS: readonly ECourseModality[] = [1, 2, 3, 4];
const MODALITY_LABEL_KEY: Record<ECourseModality, string> = {
  1: 'general.modalityOptions.distance',
  2: 'general.modalityOptions.presential',
  3: 'general.modalityOptions.semiClassroom',
  4: 'general.modalityOptions.onDemand',
};

/**
 * Seção "Geral" do módulo: empresa (autocomplete), descrição, datas (DatePicker)
 * e modalidade. Campos controlados por React Hook Form via contexto.
 */
export function GeneralSection() {
  const { t } = useTranslation('classModuleManagement');
  const { form } = useClassModuleForm();
  const { control, formState } = form;

  return (
    <div className="flex flex-col gap-xxs">
      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-2">
        <Controller
          control={control}
          name="companyId"
          render={({ field }) => (
            <CompanyAutocomplete
              label={t('general.company')}
              placeholder={t('general.companyPlaceholder')}
              value={field.value}
              onChange={id => field.onChange(id)}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <TextField
              label={`${t('general.description')}*`}
              placeholder={t('general.descriptionPlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(formState.errors.description)}
              {...(formState.errors.description
                ? { helper: t('validation.descriptionRequired') }
                : {})}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
        <Controller
          control={control}
          name="modality"
          render={({ field }) => (
            <SelectInput
              label={t('general.modality')}
              placeholder={t('general.modalityPlaceholder')}
              value={field.value !== null ? String(field.value) : ''}
              onValueChange={v => field.onChange(v ? (Number(v) as ECourseModality) : null)}
              size="md"
              fullWidth
            >
              {MODALITY_OPTIONS.map(option => (
                <SelectInputItem
                  key={option}
                  value={String(option)}
                  custom={{ root: 'text-nowrap' }}
                >
                  {t(MODALITY_LABEL_KEY[option] as 'general.modalityOptions.distance')}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />

        <Controller
          control={control}
          name="dateStart"
          render={({ field }) => (
            <DatePicker
              label={`${t('general.dateStart')}*`}
              value={field.value}
              onChange={iso => field.onChange(iso)}
              hasError={Boolean(formState.errors.dateStart)}
              {...(formState.errors.dateStart ? { helper: t('validation.dateStartRequired') } : {})}
            />
          )}
        />

        <Controller
          control={control}
          name="dateFinish"
          render={({ field }) => (
            <DatePicker
              label={t('general.dateFinish')}
              value={field.value}
              onChange={iso => field.onChange(iso)}
            />
          )}
        />
      </div>
    </div>
  );
}
